import { Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { Order } from '../../model/order';
import { AdminService, adminFailure } from '../../service/admin.service';
import { SeoService } from '../../service/seo.service';
import { MoneyPipe } from '../../money.pipe';
import { orderStatusLabel } from './status-labels';

/** The `orders` page: everything still waiting to be handled. */
@Component({
  selector: 'admin-orders-page',
  imports: [RouterLink, MoneyPipe],
  host: {
    style: 'display: contents'
  },
  template: `
    <section class="bg-title-page p-t-15 p-b-16 flex-col-c-m">
      <h1 class="l-text2 t-center">
        Список замовлень
      </h1>
    </section>

    <section class="cart bgwhite p-t-70 p-b-100">
      <div class="container">
        @if (failure(); as failure) {
          <p class="s-text5 text-danger text-center p-b-20">{{failure}}</p>
        }
        @if (orders(); as orders) {
          <div class="wrap-table-shopping-cart bgwhite">
            <table class="table-shopping-cart">
              <tbody>
                <tr class="table-head">
                  <th class="column-2">Замовлення</th>
                  <th class="column-2">Дата</th>
                  <th class="column-2">Одержувач</th>
                  <th class="column-3">Телефон</th>
                  <th class="column-3 text-center">Сума</th>
                  <th class="column-3 text-center">Статус</th>
                </tr>
                @for (order of orders; track order.code) {
                  <tr class="table-row admin-order">
                    <td class="column-2">
                      <a [routerLink]="['/orders', order.code]">№{{order.code}}</a>
                    </td>
                    <td class="column-2">{{displayedTime(order.time)}}</td>
                    <td class="column-2">
                      {{order.shipping?.address?.firstName}} {{order.shipping?.address?.lastName}}
                    </td>
                    <td class="column-3">{{order.shipping?.address?.phone}}</td>
                    <td class="column-3 text-center">{{order.total | money}}</td>
                    <td class="column-3 text-center">{{label(order.status)}}</td>
                  </tr>
                } @empty {
                  <tr class="table-row">
                    <td class="column-2" colspan="6">Немає замовлень в опрацюванні.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else if (!failure()) {
          <p class="text-center">Завантаження…</p>
        }
      </div>
    </section>
    `
})
export class AdminOrdersPageComponent {

  readonly service = inject(AdminService);
  readonly orders = signal<Order[] | undefined>(undefined);
  readonly failure = signal<string | undefined>(undefined);

  readonly label = orderStatusLabel;

  /** The API sends Java's ZonedDateTime, `…+03:00[Europe/Kyiv]`; the bracketed zone is not valid for Date. */
  displayedTime(time: string | undefined): string {
    if (!time) {
      return '';
    }
    const zone = /\[(.+)]$/.exec(time)?.[1];
    const date = new Date(time.replace(/\[.+]$/, ''));
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString('uk-UA', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: zone
    });
  }

  constructor() {
    const seo = inject(SeoService);
    seo.apply({ title: 'Список замовлень', robots: 'noindex,nofollow' }, true);
    seo.openGraph(undefined);
    void this.service.orders().then(
      orders => this.orders.set(orders),
      error => this.failure.set(adminFailure(error, 'Не вдалося завантажити замовлення. Спробуйте ще раз.')));
  }

}
